import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { loadPerformanceSource, type PerformanceSource } from '@/utils/performanceSource';
import { derivePerformanceData, type DerivedPerformance } from '@/utils/performanceData';

type Status = 'loading' | 'ready' | 'error';

interface PerformanceContextValue {
  status: Status;
  error: string | null;
  data: DerivedPerformance | null;
  reload: () => void;
}

const PerformanceContext = createContext<PerformanceContextValue | undefined>(undefined);

/**
 * Loads the external performance data once for the whole app and shares the
 * derived results with any component that needs them. Because the raw data
 * lives in `public/data/performance.json`, replacing that single file on the
 * server is enough to refresh every figure on the site.
 */
export function PerformanceProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<PerformanceSource | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setStatus('loading');
    setError(null);

    loadPerformanceSource()
      .then((loaded) => {
        if (cancelled) return;
        setSource(loaded);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'The trading data could not be loaded.');
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const data = useMemo(() => (source ? derivePerformanceData(source) : null), [source]);
  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const value = useMemo(
    () => ({ status, error, data, reload }),
    [status, error, data, reload],
  );

  return <PerformanceContext.Provider value={value}>{children}</PerformanceContext.Provider>;
}

export function usePerformance(): PerformanceContextValue {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used inside a PerformanceProvider.');
  }
  return context;
}